/**
 * Companion Widget Context — Spec 10-1
 * Global state for the persistent floating widget.
 * Provides open/close/prefill API that other components can call.
 */
import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export interface WidgetPageContext {
  family_id: string;
  family_name: string;
}

interface StevensWidgetState {
  isOpen: boolean;
  prefillText: string;
  voiceOnOpen: boolean;
  open: (prefillText?: string) => void;
  openVoice: () => void;
  clearVoiceOnOpen: () => void;
  /** Reset an unconsumed prefill — fired after send and on close-unsent (#929). */
  clearPrefill: () => void;
  close: () => void;
  toggle: () => void;
  /** Family context set when TRFer is viewing a family profile page */
  pageContext: WidgetPageContext | null;
  setPageContext: (ctx: WidgetPageContext | null) => void;
  /** True when widget should not render (e.g. on /chat page or /trfer/williams) */
  hidden: boolean;
}

const StevensWidgetContext = createContext<StevensWidgetState | null>(null);

const STORAGE_KEY = 'stevens_widget_open';

/** Routes that are themselves a full chat surface. */
const FULL_CHAT_ROUTES = ['/chat', '/trfer/williams'];

function isWidgetHiddenPath(pathname: string): boolean {
  return FULL_CHAT_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

export function StevensWidgetProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; }
  });
  const [prefillText, setPrefillText] = useState('');
  const [voiceOnOpen, setVoiceOnOpen] = useState(false);
  const [pageContext, setPageContext] = useState<WidgetPageContext | null>(null);

  // A page that IS the chat never carries the floating widget too. The match
  // covers the thread path `/chat/:conversationId` (ChatPage rewrites `/chat`
  // to it as soon as a thread exists), otherwise a widget left open on another
  // page reopened over the chat with a second composer.
  const hidden = isWidgetHiddenPath(location.pathname);

  const open = useCallback((text?: string) => {
    setPrefillText(text || '');
    setIsOpen(true);
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch { /* noop */ }
  }, []);

  const openVoice = useCallback(() => {
    setVoiceOnOpen(true);
    setIsOpen(true);
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch { /* noop */ }
  }, []);

  const clearVoiceOnOpen = useCallback(() => setVoiceOnOpen(false), []);

  const clearPrefill = useCallback(() => setPrefillText(''), []);

  const close = useCallback(() => {
    setIsOpen(false);
    try { localStorage.setItem(STORAGE_KEY, 'false'); } catch { /* noop */ }
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ isOpen, prefillText, voiceOnOpen, open, openVoice, clearVoiceOnOpen, clearPrefill, close, toggle, pageContext, setPageContext, hidden }),
    [isOpen, prefillText, voiceOnOpen, open, openVoice, clearVoiceOnOpen, clearPrefill, close, toggle, pageContext, setPageContext, hidden],
  );

  return (
    <StevensWidgetContext.Provider value={value}>
      {children}
    </StevensWidgetContext.Provider>
  );
}

export function useStevensWidget() {
  const ctx = useContext(StevensWidgetContext);
  if (!ctx) throw new Error('useStevensWidget must be used within StevensWidgetProvider');
  return ctx;
}
